import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../context/AuthContext";

// ── Constants (same as TemplateBuilder) ───────────────────────────────────────
const QUESTION_TYPES = [
  { value: "yes_no",        label: "Yes / No" },
  { value: "yes_no_na",     label: "Yes / No / N/A" },
  { value: "numeric",       label: "Numeric" },
  { value: "text",          label: "Text" },
  { value: "date",          label: "Date" },
  { value: "single_choice", label: "Single choice" },
  { value: "multi_choice",  label: "Multi choice" },
];

const COMPLIANCE_RULES = [
  { value: "equals",                label: "Answer equals" },
  { value: "not_equals",            label: "Answer does not equal" },
  { value: "greater_than",          label: "Number greater than" },
  { value: "less_than",             label: "Number less than" },
  { value: "between",               label: "Number between (e.g. 0,100)" },
  { value: "date_on_or_after",      label: "Date on or after" },
  { value: "date_before",           label: "Date before" },
  { value: "date_within_last_days", label: "Date within last N days" },
  { value: "required_only",         label: "Required (any answer passes)" },
];

const SEVERITIES = [
  { value: "minor",    label: "Minor" },
  { value: "major",    label: "Major" },
  { value: "critical", label: "Critical" },
];

const TRIGGER_VALUES = [
  { value: "yes", label: "Yes" },
  { value: "no",  label: "No" },
  { value: "na",  label: "N/A" },
];

function initials(str, n = 2) {
  const words = (str || "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  if (words.length === 1) return words[0].slice(0, n).toUpperCase();
  return words.slice(0, n).map(w => w[0]?.toUpperCase() || "").join("");
}

function questionCodeForSection(prefix, sectionTitle, index) {
  const sectionPart = initials(sectionTitle, 2) || "XX";
  const num = String(index + 1).padStart(3, "0");
  return `${prefix}-${sectionPart}-${num}`;
}

function generateId() {
  return Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
}

// ── Convert API response → editor state ───────────────────────────────────────
function apiToEditorState(data) {
  const allQuestions = [];
  const sections = (data.sections || []).map((sec) => {
    const secId = generateId();
    const topLevelQIds = [];

    (sec.questions || [])
      .filter((q) => !q.parent_id)
      .forEach((q) => {
        const qId = generateId();
        const followUpIds = [];

        (sec.questions || [])
          .filter((fq) => fq.parent_id === q.id)
          .forEach((fq) => {
            const fqId = generateId();
            allQuestions.push({
              _id: fqId,
              sectionId: secId,
              code: fq.code || "",
              question_text: fq.text || "",
              help_text: fq.help_text || "",
              question_type: fq.type || "yes_no",
              required: fq.required !== false,
              compliance_rule: fq.compliance_rule || "equals",
              expected_value: fq.expected_value || "yes",
              severity: fq.severity || "major",
              options: (fq.options || []).map((o) => ({
                _id: generateId(),
                label: o.label,
                value: o.value,
              })),
              followUps: [],
              parentTriggerValue: fq.parent_trigger || "no",
            });
            followUpIds.push(fqId);
          });

        allQuestions.push({
          _id: qId,
          sectionId: secId,
          code: q.code || "",
          question_text: q.text || "",
          help_text: q.help_text || "",
          question_type: q.type || "yes_no",
          required: q.required !== false,
          compliance_rule: q.compliance_rule || "equals",
          expected_value: q.expected_value || "yes",
          severity: q.severity || "major",
          options: (q.options || []).map((o) => ({
            _id: generateId(),
            label: o.label,
            value: o.value,
          })),
          followUps: followUpIds,
          parentTriggerValue: "no",
        });
        topLevelQIds.push(qId);
      });

    return { _id: secId, title: sec.title || "", _questionIds: topLevelQIds };
  });

  return { sections, questions: allQuestions };
}

// ── Sub-components (same as TemplateBuilder) ──────────────────────────────────
function SectionHeader({ section, index, total, onChange, onDelete, onMoveUp, onMoveDown }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className="flex-1">
        <input
          type="text"
          placeholder={`Section ${index + 1} title`}
          value={section.title}
          onChange={(e) => onChange({ ...section, title: e.target.value })}
          className="w-full border rounded px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-gray-400"
        />
      </div>
      <button type="button" onClick={onMoveUp}   disabled={index === 0}         className="px-2 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-30">↑</button>
      <button type="button" onClick={onMoveDown} disabled={index === total - 1} className="px-2 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-30">↓</button>
      <button type="button" onClick={onDelete}   className="px-2 py-1 text-xs border rounded hover:bg-red-50 text-red-500">✕</button>
    </div>
  );
}

function newOption() {
  return { _id: generateId(), label: "", value: "" };
}

function labelToValue(label) {
  return label.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
}

function rulesForType(questionType) {
  switch (questionType) {
    case "yes_no":
    case "yes_no_na":
      return COMPLIANCE_RULES.filter(r => ["equals", "not_equals", "required_only"].includes(r.value));
    case "numeric":
      return COMPLIANCE_RULES.filter(r => ["equals", "not_equals", "greater_than", "less_than", "between", "required_only"].includes(r.value));
    case "date":
      return COMPLIANCE_RULES.filter(r => ["date_on_or_after", "date_before", "date_within_last_days", "required_only"].includes(r.value));
    case "text":
      return COMPLIANCE_RULES.filter(r => ["equals", "not_equals", "required_only"].includes(r.value));
    default:
      return COMPLIANCE_RULES.filter(r => ["equals", "not_equals", "required_only"].includes(r.value));
  }
}

function ChoiceEditor({ options, onChange, question, onQuestionUpdate, isMulti }) {
  function addOption() { onChange([...options, newOption()]); }
  function updateOptionLabel(idx, label) {
    const val = labelToValue(label);
    onChange(options.map((o, i) => i === idx ? { ...o, label, value: val } : o));
  }
  function removeOption(idx) { onChange(options.filter((_, i) => i !== idx)); }

  const expectedVals = question.expected_value
    ? question.expected_value.split(",").map(v => v.trim()).filter(Boolean)
    : [];

  function toggleExpected(optValue) {
    let next;
    if (isMulti) {
      next = expectedVals.includes(optValue)
        ? expectedVals.filter(v => v !== optValue).join(",")
        : [...expectedVals, optValue].join(",");
    } else {
      next = expectedVals.includes(optValue) ? "" : optValue;
    }
    onQuestionUpdate({ ...question, compliance_rule: next ? "equals" : "", expected_value: next });
  }

  return (
    <div className="mt-2 space-y-1">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs text-gray-400 w-4 shrink-0" />
        <span className="flex-1 text-xs text-gray-400">Choice</span>
        <span className="text-xs text-gray-400 w-20 text-center">Expected?</span>
        <span className="w-5" />
      </div>
      {options.map((opt, idx) => {
        const isChecked = expectedVals.includes(opt.value) && opt.value !== "";
        return (
          <div key={opt._id} className="flex items-center gap-2">
            <span className="text-xs text-gray-400 w-4 shrink-0">{idx + 1}.</span>
            <input type="text" placeholder="Type option here…" value={opt.label}
              onChange={(e) => updateOptionLabel(idx, e.target.value)}
              className="flex-1 border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-gray-300" />
            <div className="w-20 flex justify-center">
              <input type={isMulti ? "checkbox" : "radio"} checked={isChecked}
                disabled={!opt.value} onChange={() => toggleExpected(opt.value)}
                title={opt.value ? "Mark as expected answer" : "Fill in the option first"}
                className="cursor-pointer" />
            </div>
            <button type="button" onClick={() => removeOption(idx)} className="text-red-400 hover:text-red-600 text-xs px-1 w-5">✕</button>
          </div>
        );
      })}
      <button type="button" onClick={addOption} className="text-xs text-gray-500 hover:text-gray-700 border border-dashed rounded px-2 py-1 mt-1">
        + Add option
      </button>
    </div>
  );
}

function QuestionCard({
  question, index, total, sectionId,
  onUpdate, onDelete, onMoveUp, onMoveDown,
  onAddFollowUp, isFollowUp, parentTriggerValue, onParentTriggerChange,
  allQuestions,
}) {
  const [expanded, setExpanded] = useState(true);
  const isChoice = ["single_choice", "multi_choice"].includes(question.question_type);
  const followUps = (question.followUps || [])
    .map(fid => allQuestions.find(q => q._id === fid))
    .filter(Boolean);

  return (
    <div className={`border rounded-lg bg-white ${isFollowUp ? "ml-6 border-l-4 border-l-blue-200" : ""}`}>
      <div className="flex items-center gap-1 px-2 py-2 border-b bg-gray-50 rounded-t-lg min-w-0 overflow-hidden">
        {isFollowUp && (
          <span className="text-xs text-blue-500 font-medium shrink-0">↳ if {parentTriggerValue}</span>
        )}
        <button type="button" onClick={() => setExpanded(e => !e)} className="flex-1 min-w-0 text-left text-sm font-medium truncate">
          {question.question_text || <span className="text-gray-400 italic">New question</span>}
        </button>
        <span className="hidden sm:inline text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded font-mono shrink-0">
          {question.question_type}
        </span>
        <button type="button" onClick={onMoveUp}   disabled={index === 0}         className="px-1.5 py-0.5 text-xs border rounded hover:bg-white disabled:opacity-30" title="Move up">↑</button>
        <button type="button" onClick={onMoveDown} disabled={index === total - 1} className="px-1.5 py-0.5 text-xs border rounded hover:bg-white disabled:opacity-30" title="Move down">↓</button>
        <button type="button" onClick={() => setExpanded(e => !e)} className="text-xs text-gray-400 px-1">{expanded ? "▲" : "▼"}</button>
        <button type="button" onClick={onDelete} className="text-xs text-red-400 hover:text-red-600 px-1" title="Delete question">✕</button>
      </div>

      {expanded && (
        <div className="px-3 py-3 space-y-3">
          {isFollowUp && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500 w-32 shrink-0">Show when parent is</label>
              <select value={parentTriggerValue} onChange={(e) => onParentTriggerChange(e.target.value)}
                className="border rounded px-2 py-1 text-xs">
                {TRIGGER_VALUES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <input type="text" placeholder="Auto" value={question.code} readOnly
              className="w-full sm:w-28 border rounded px-2 py-1.5 text-xs font-mono bg-gray-50 text-gray-400 cursor-default"
              title="Question code" />
            <input type="text" placeholder="Question text *" value={question.question_text}
              onChange={(e) => onUpdate({ ...question, question_text: e.target.value })}
              className="flex-1 min-w-0 border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300" />
          </div>

          <input type="text" placeholder="Help text (optional — shown to inspector below the question)"
            value={question.help_text}
            onChange={(e) => onUpdate({ ...question, help_text: e.target.value })}
            className="w-full border rounded px-2 py-1.5 text-xs text-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-300" />

          <div className="flex gap-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <label className="text-xs text-gray-500">Type</label>
              <select value={question.question_type}
                onChange={(e) => {
                  const newType = e.target.value;
                  const allowed = rulesForType(newType).map(r => r.value);
                  const rule = allowed.includes(question.compliance_rule) ? question.compliance_rule : "";
                  let options = [];
                  if (newType === "single_choice") options = [newOption(), newOption()];
                  if (newType === "multi_choice") options = [newOption(), newOption(), newOption()];
                  const expectedValue = (newType === "yes_no" || newType === "yes_no_na") ? "yes" : "";
                  onUpdate({ ...question, question_type: newType, options, compliance_rule: rule, expected_value: expectedValue });
                }}
                className="border rounded px-2 py-1 text-xs">
                {QUESTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-1.5">
              <label className="text-xs text-gray-500">Required</label>
              <input type="checkbox" checked={question.required}
                onChange={(e) => onUpdate({ ...question, required: e.target.checked })}
                className="rounded" />
            </div>
          </div>

          {isChoice && (
            <div>
              <div className="text-xs text-gray-500 mb-1">Answer options</div>
              <ChoiceEditor options={question.options}
                onChange={(opts) => onUpdate({ ...question, options: opts })}
                question={question} onQuestionUpdate={onUpdate}
                isMulti={question.question_type === "multi_choice"} />
            </div>
          )}

          <div className="border-t pt-3 space-y-2">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Compliance rule</div>

            {(question.question_type === "yes_no" || question.question_type === "yes_no_na") && (
              <div className="flex gap-2 flex-wrap items-end">
                <div className="flex items-center gap-1.5">
                  <label className="text-xs text-gray-500">Expected answer</label>
                  <select
                    value={question.compliance_rule === "required_only" ? "either" : question.expected_value || "yes"}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "either") {
                        onUpdate({ ...question, compliance_rule: "required_only", expected_value: "" });
                      } else {
                        onUpdate({ ...question, compliance_rule: "equals", expected_value: val });
                      }
                    }}
                    className="border rounded px-2 py-1 text-xs">
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="either">Either (any answer passes)</option>
                  </select>
                </div>
                {question.compliance_rule !== "required_only" && (
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs text-gray-500">Severity if fails</label>
                    <select value={question.severity}
                      onChange={(e) => onUpdate({ ...question, severity: e.target.value })}
                      className="border rounded px-2 py-1 text-xs">
                      {SEVERITIES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                )}
              </div>
            )}

            {(question.question_type === "single_choice" || question.question_type === "multi_choice") && (
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-gray-500">Severity if fails</label>
                <select value={question.severity}
                  onChange={(e) => onUpdate({ ...question, severity: e.target.value })}
                  className="border rounded px-2 py-1 text-xs">
                  {SEVERITIES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            )}

            {question.question_type === "date" && (
              <div className="flex gap-2 flex-wrap items-end">
                <div className="flex items-center gap-1.5">
                  <label className="text-xs text-gray-500">Rule</label>
                  <select value={question.compliance_rule || ""}
                    onChange={(e) => onUpdate({ ...question, compliance_rule: e.target.value, expected_value: "" })}
                    className="border rounded px-2 py-1 text-xs">
                    <option value="">— no rule —</option>
                    <option value="date_on_or_after">Date on or after</option>
                    <option value="date_before">Date before</option>
                    <option value="date_within_last_days">Date within last N days</option>
                    <option value="required_only">Required (any date passes)</option>
                  </select>
                </div>
                {question.compliance_rule === "date_within_last_days" && (
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs text-gray-500">Number of days</label>
                    <input type="number" min="1" placeholder="e.g. 365" value={question.expected_value || ""}
                      onChange={(e) => onUpdate({ ...question, expected_value: e.target.value })}
                      className="w-24 border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-gray-300" />
                  </div>
                )}
                {(question.compliance_rule === "date_on_or_after" || question.compliance_rule === "date_before") && (
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs text-gray-500">Date</label>
                    <input type="date" value={question.expected_value || ""}
                      onChange={(e) => onUpdate({ ...question, expected_value: e.target.value })}
                      className="border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-gray-300" />
                  </div>
                )}
                {question.compliance_rule && question.compliance_rule !== "required_only" && (
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs text-gray-500">Severity if fails</label>
                    <select value={question.severity}
                      onChange={(e) => onUpdate({ ...question, severity: e.target.value })}
                      className="border rounded px-2 py-1 text-xs">
                      {SEVERITIES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                )}
              </div>
            )}

            {(question.question_type === "numeric" || question.question_type === "text") && (
              <div className="flex gap-2 flex-wrap items-end">
                <div className="flex items-center gap-1.5">
                  <label className="text-xs text-gray-500">Rule</label>
                  <select value={question.compliance_rule || ""}
                    onChange={(e) => onUpdate({ ...question, compliance_rule: e.target.value })}
                    className="border rounded px-2 py-1 text-xs">
                    <option value="">— no rule —</option>
                    {rulesForType(question.question_type).map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                {question.compliance_rule && question.compliance_rule !== "required_only" && (
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs text-gray-500">
                      {question.compliance_rule === "between" ? "Min,Max" : "Expected value"}
                    </label>
                    <input type="text"
                      placeholder={question.compliance_rule === "between" ? "e.g. 0,100" : question.question_type === "numeric" ? "e.g. 1" : "value"}
                      value={question.expected_value || ""}
                      onChange={(e) => onUpdate({ ...question, expected_value: e.target.value })}
                      className="w-32 border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-gray-300" />
                  </div>
                )}
                {question.compliance_rule && question.compliance_rule !== "required_only" && (
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs text-gray-500">Severity if fails</label>
                    <select value={question.severity}
                      onChange={(e) => onUpdate({ ...question, severity: e.target.value })}
                      className="border rounded px-2 py-1 text-xs">
                      {SEVERITIES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>

          {!isFollowUp && (
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Follow-up questions ({followUps.length})
                </div>
                <button type="button" onClick={() => onAddFollowUp(question._id)}
                  className="text-xs px-2 py-1 border rounded hover:bg-gray-50 text-gray-600">
                  + Add follow-up
                </button>
              </div>
              {followUps.map((fq, fi) => (
                <QuestionCard
                  key={fq._id} question={fq} index={fi} total={followUps.length}
                  sectionId={sectionId} isFollowUp={true}
                  parentTriggerValue={fq.parentTriggerValue}
                  onParentTriggerChange={(val) => onUpdate({ ...fq, parentTriggerValue: val }, true)}
                  allQuestions={allQuestions}
                  onUpdate={(updated, _isChild) => onUpdate(updated, true)}
                  onDelete={() => onUpdate({ ...question, followUps: question.followUps.filter(id => id !== fq._id) }, false, fq._id)}
                  onMoveUp={() => {
                    if (fi === 0) return;
                    const ids = [...question.followUps];
                    [ids[fi - 1], ids[fi]] = [ids[fi], ids[fi - 1]];
                    onUpdate({ ...question, followUps: ids });
                  }}
                  onMoveDown={() => {
                    if (fi === followUps.length - 1) return;
                    const ids = [...question.followUps];
                    [ids[fi], ids[fi + 1]] = [ids[fi + 1], ids[fi]];
                    onUpdate({ ...question, followUps: ids });
                  }}
                  onAddFollowUp={() => {}}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function TemplateEditPage() {
  const { templateId } = useParams();
  const { token, user, activeCompany } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Template metadata
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [questionPrefix, setQuestionPrefix] = useState("");
  const [description, setDescription] = useState("");
  const [originalVersion, setOriginalVersion] = useState(1);
  const [hasInspections, setHasInspections] = useState(false);

  async function generateCodes() {
    if (!name.trim()) { setErr("Enter a template name first."); return; }
    setCodeLoading(true);
    setErr(null);
    try {
      const res = await api("/india/templates/count", { token });
      const count = (res.count || 0) + 1;
      const countStr = String(count).padStart(3, "0");
      const companyInitials = initials(activeCompany?.name || "XX", 2);
      const templateInitials = initials(name, 2);
      const generatedCode = `${companyInitials}-${countStr}-${templateInitials}`;
      const generatedPrefix = templateInitials;
      setCode(generatedCode);
      setQuestionPrefix(generatedPrefix);
      setQuestions(prev => prev.map(q => {
        const sec = sections.find(s => s._id === q.sectionId);
        const sectionQs = prev.filter(qq => qq.sectionId === q.sectionId && !prev.some(p => p.followUps?.includes(qq._id)));
        const idx = sectionQs.findIndex(qq => qq._id === q._id);
        return { ...q, code: questionCodeForSection(generatedPrefix, sec?.title || "", idx) };
      }));
    } catch (e) {
      setErr(e?.message || "Failed to generate code");
    } finally {
      setCodeLoading(false);
    }
  }

  // Builder state
  const [sections, setSections] = useState([]);
  const [questions, setQuestions] = useState([]);

  // Load existing template
  useEffect(() => {
    if (!token || !templateId) return;
    setLoading(true);
    api(`/india/templates/${templateId}`, { token })
      .then((data) => {
        if (data.created_by_user_id != null && user?.id != null &&
            Number(data.created_by_user_id) !== Number(user.id)) {
          setErr("You do not own this template.");
          return;
        }
        setName(data.name || "");
        setCode(data.code || "");
        setDescription(data.description || "");
        setOriginalVersion(data.version || 1);
        const { sections: secs, questions: qs } = apiToEditorState(data);
        setSections(secs);
        setQuestions(qs);
        // Check if template has been used in inspections
        api(`/india/templates/${templateId}/usage`, { token })
          .then((usage) => setHasInspections(usage?.inspection_count > 0))
          .catch(() => { /* non-fatal — will be caught on save instead */ });
      })
      .catch(() => setErr("Failed to load template."))
      .finally(() => setLoading(false));
  }, [token, templateId, user?.id]);

  // ── Builder operations (same as TemplateBuilder) ──────────────────────────
  function topLevelQuestionsForSection(secId) {
    const sec = sections.find(s => s._id === secId);
    if (!sec) return [];
    return (sec._questionIds || []).map(qid => questions.find(q => q._id === qid)).filter(Boolean);
  }

  function addSection() {
    const s = { _id: generateId(), title: "", _questionIds: [] };
    setSections(prev => [...prev, s]);
  }

  function updateSection(idx, updated) {
    setSections(prev => prev.map((s, i) => i === idx ? updated : s));
  }

  function deleteSection(idx) {
    const sec = sections[idx];
    const allQidsToRemove = new Set();
    function collectIds(qid) {
      allQidsToRemove.add(qid);
      const q = questions.find(q => q._id === qid);
      if (q) (q.followUps || []).forEach(collectIds);
    }
    (sec._questionIds || []).forEach(collectIds);
    setSections(prev => prev.filter((_, i) => i !== idx));
    setQuestions(prev => prev.filter(q => !allQidsToRemove.has(q._id)));
  }

  function moveSection(idx, dir) {
    setSections(prev => {
      const next = [...prev];
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return next;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next;
    });
  }

  function addQuestion(secId) {
    const q = { _id: generateId(), sectionId: secId, code: "", question_text: "", help_text: "",
      question_type: "yes_no", required: true, compliance_rule: "equals", expected_value: "yes",
      severity: "major", options: [], followUps: [], parentTriggerValue: "no" };
    setQuestions(prev => [...prev, q]);
    setSections(prev => prev.map(s => s._id === secId ? { ...s, _questionIds: [...(s._questionIds || []), q._id] } : s));
  }

  function updateQuestion(updated) {
    setQuestions(prev => prev.map(q => q._id === updated._id ? updated : q));
  }

  function deleteQuestion(qid) {
    const q = questions.find(q => q._id === qid);
    const toRemove = new Set([qid, ...(q?.followUps || [])]);
    setQuestions(prev => prev.filter(q => !toRemove.has(q._id)));
    setSections(prev => prev.map(s => ({ ...s, _questionIds: (s._questionIds || []).filter(id => !toRemove.has(id)) })));
  }

  function moveQuestion(secId, qid, dir) {
    setSections(prev => prev.map(s => {
      if (s._id !== secId) return s;
      const ids = [...(s._questionIds || [])];
      const idx = ids.indexOf(qid);
      const swap = idx + dir;
      if (swap < 0 || swap >= ids.length) return s;
      [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
      return { ...s, _questionIds: ids };
    }));
  }

  function addFollowUp(parentQid) {
    const parent = questions.find(q => q._id === parentQid);
    const fq = { _id: generateId(), sectionId: parent?.sectionId, code: "", question_text: "", help_text: "",
      question_type: "yes_no", required: true, compliance_rule: "equals", expected_value: "yes",
      severity: "major", options: [], followUps: [], parentTriggerValue: "no" };
    setQuestions(prev => [...prev, fq]);
    updateQuestion({ ...parent, followUps: [...(parent?.followUps || []), fq._id] });
  }

  // ── Build payload ─────────────────────────────────────────────────────────
  function buildPayload(overrideName) {
    function buildQ(q, qi) {
      return {
        code: q.code.trim() || null,
        question_text: q.question_text.trim(),
        help_text: q.help_text.trim() || null,
        question_type: q.question_type,
        required: q.required,
        sort_order: qi,
        compliance_rule: q.compliance_rule || null,
        expected_value: q.expected_value?.trim() || null,
        severity: q.severity,
        options: q.options.map((o, oi) => ({ label: o.label.trim(), value: o.value.trim(), sort_order: oi })),
        follow_ups: (q.followUps || []).map(fid => questions.find(fq => fq._id === fid)).filter(Boolean)
          .map((fq, fi) => ({ ...buildQ(fq, fi), parent_trigger_value: fq.parentTriggerValue || "no" })),
      };
    }
    return {
      name: (overrideName || name).trim(),
      code: code.trim() || null,
      description: description.trim() || null,
      sections: sections.map((sec, si) => ({
        title: sec.title.trim(),
        sort_order: si,
        questions: topLevelQuestionsForSection(sec._id).map((q, qi) => buildQ(q, qi)),
      })),
    };
  }

  function validate() {
    if (!name.trim()) return "Template name is required.";
    if (sections.length === 0) return "Add at least one section.";
    for (const sec of sections) {
      if (!sec.title.trim()) return "All sections need a title.";
      if (topLevelQuestionsForSection(sec._id).length === 0) return `Section "${sec.title}" has no questions.`;
      for (const q of topLevelQuestionsForSection(sec._id)) {
        if (!q.question_text.trim()) return "All questions need question text.";
      }
    }
    return null;
  }

  async function handleUpdate() {
    const validationErr = validate();
    if (validationErr) { setErr(validationErr); return; }
    setShowModal(true);
  }

  async function saveUpdate() {
    setSaving(true);
    setErr(null);
    try {
      await api(`/india/templates/${templateId}`, {
        token, method: "PUT", body: buildPayload(),
      });
      navigate("/templates", { state: { created: templateId, name: name.trim() } });
    } catch (e) {
      setErr(e?.message || "Save failed");
    } finally {
      setSaving(false);
      setShowModal(false);
    }
  }

  async function saveAsNew() {
    setSaving(true);
    setErr(null);
    try {
      const payload = buildPayload(name.trim());
      payload.code = null; // code must be unique — clear it on duplicate
      const result = await api(`/india/templates/${templateId}/duplicate`, {
        token, method: "POST", body: payload,
      });
      navigate("/templates", { state: { created: result.template_id, name: name.trim() } });
    } catch (e) {
      setErr(e?.message || "Save failed");
    } finally {
      setSaving(false);
      setShowModal(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) return <div className="pt-8 px-6 text-sm text-gray-500">Loading template…</div>;
  if (err && !sections.length) return <div className="pt-8 px-6 text-sm text-red-600">{err}</div>;

  return (
    <div className="pt-4 sm:pt-8 px-3 sm:px-6 max-w-4xl mx-auto pb-16">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <button type="button" onClick={() => navigate("/templates")}
            className="text-xs text-gray-400 hover:text-gray-600 mb-1 block">
            ← Back to templates
          </button>
          <h1 className="text-xl font-semibold">Edit template</h1>
          <p className="text-xs text-gray-400 mt-0.5">Edit template — save in place or save as a new template with a different name.</p>
        </div>
        <button type="button" onClick={handleUpdate} disabled={saving}
          className="px-4 py-2 rounded bg-gray-900 text-white text-sm hover:bg-gray-700 disabled:opacity-50">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      {err && (
        <div className="mb-4 text-sm text-red-700 border border-red-200 bg-red-50 rounded-lg px-4 py-3">{err}</div>
      )}

      {/* Template metadata */}
      <section className="border border-gray-200 rounded-xl bg-white p-5 mb-6 space-y-3">
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Template details</div>

        <div className="flex gap-3">
          <div className="flex-1">
            <label className="text-xs text-gray-500 block mb-1">Name *</label>
            <input type="text" placeholder="e.g. Engine room baseline v1" value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400" />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <label className="text-xs text-gray-500 block mb-1">Template code</label>
            <input type="text" placeholder="e.g. KI-014-ER" value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-gray-400" />
          </div>
          <div className="w-full sm:w-32">
            <label className="text-xs text-gray-500 block mb-1">Question prefix</label>
            <input type="text" placeholder="e.g. ER" maxLength={6} value={questionPrefix}
              onChange={(e) => setQuestionPrefix(e.target.value.toUpperCase())}
              className="w-full border rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-gray-400" />
          </div>
          <button type="button" onClick={generateCodes} disabled={codeLoading || !name.trim()}
            className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap">
            {codeLoading ? "Generating…" : "✦ Auto-generate"}
          </button>
        </div>
        <div className="text-xs text-gray-400">
          Auto-generate builds the code from your company initials + template count + template name initials. The question prefix drives all question codes.
        </div>

        <div>
          <label className="text-xs text-gray-500 block mb-1">Description</label>
          <input type="text" placeholder="Optional description" value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400" />
        </div>
      </section>

      {/* Sections */}
      <div className="space-y-6">
        {sections.map((sec, si) => {
          const sectionQs = topLevelQuestionsForSection(sec._id);
          return (
            <section key={sec._id} className="border border-gray-200 rounded-xl bg-gray-50 p-4">
              <SectionHeader section={sec} index={si} total={sections.length}
                onChange={(updated) => updateSection(si, updated)}
                onDelete={() => deleteSection(si)}
                onMoveUp={() => moveSection(si, -1)}
                onMoveDown={() => moveSection(si, 1)} />
              <div className="space-y-3">
                {sectionQs.map((q, qi) => (
                  <QuestionCard key={q._id} question={q} index={qi} total={sectionQs.length}
                    sectionId={sec._id} allQuestions={questions} isFollowUp={false}
                    onUpdate={updateQuestion}
                    onDelete={() => deleteQuestion(q._id)}
                    onMoveUp={() => moveQuestion(sec._id, q._id, -1)}
                    onMoveDown={() => moveQuestion(sec._id, q._id, 1)}
                    onAddFollowUp={addFollowUp} />
                ))}
                {sectionQs.length === 0 && <div className="text-xs text-gray-400 italic px-1">No questions yet.</div>}
                <button type="button" onClick={() => addQuestion(sec._id)}
                  className="w-full text-sm border border-dashed border-gray-300 rounded-lg py-2 text-gray-500 hover:bg-white hover:border-gray-400 transition">
                  + Add question
                </button>
              </div>
            </section>
          );
        })}
      </div>

      <button type="button" onClick={addSection}
        className="mt-4 w-full text-sm border border-dashed border-gray-300 rounded-xl py-3 text-gray-500 hover:bg-gray-50 hover:border-gray-400 transition">
        + Add section
      </button>

      <div className="mt-8 flex justify-end">
        <button type="button" onClick={handleUpdate} disabled={saving}
          className="px-6 py-2 rounded bg-gray-900 text-white text-sm hover:bg-gray-700 disabled:opacity-50">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      {/* Save modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-base font-semibold mb-1">How do you want to save?</h2>
            <p className="text-sm text-gray-500 mb-5">
              You can overwrite the existing template, or keep the original and save your changes as a new version.
            </p>
            <div className="space-y-3 mb-5">
              <div className={`border rounded-xl p-4 ${hasInspections ? "opacity-50" : ""}`}>
                <div className="text-sm font-medium mb-0.5">Update existing</div>
                <div className="text-xs text-gray-500">
                  {hasInspections
                    ? "This template has been used in inspections and cannot be overwritten."
                    : `Overwrites v${originalVersion}. Any inspections using this template will see the updated questions.`}
                </div>
              </div>
              <div className="border rounded-xl p-4">
                <div className="text-sm font-medium mb-0.5">Save as new template</div>
                <div className="text-xs text-gray-500">Saves a new template using the name you entered above. The original is kept intact and existing inspections are unaffected.</div>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShowModal(false)} disabled={saving}
                className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button type="button" onClick={saveUpdate} disabled={saving || hasInspections}
                className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                {saving ? "Saving…" : `Update existing (v${originalVersion})`}
              </button>
              <button type="button" onClick={saveAsNew} disabled={saving}
                className="px-4 py-2 text-sm rounded-lg bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50">
                {saving ? "Saving…" : "Save as new template"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
